// BroadcastBoard.jsx
//
// The streamed dashboard — June Dashboard layout with canned props, rendered
// once on the encoder droplet and broadcast to YouTube. NO device identity:
// no device prop, no /parks fetch, no socket, no useRemote/useTV/store.
//
// Differences from Dashboard.jsx (June):
//   - ALL parks, hardcoded (ALL_PARK_KEYS below)
//   - Header: "Have a Magical Day" kept, family-name line EMPTY (device
//     overlays the name locally), EXIT button removed (device adds its own)
//   - VacationCountdownTile -> empty styled frame (device overlays days-left)
//   - LiveStreamTile -> empty styled frame (device embeds its own player)
//   - Fixed 1280x800 display layout, scale 1. 16:9 padding happens at the
//     encoder (ffmpeg pads to 1920x1080 with BG color), not here.
//
// This route is loaded ONLY by the encoder droplet's Chromium via the
// /broadcast path (wired in index.js). Devices never load it directly.

import ParkPills from "./ParkPills";
import NewsHero from "./NewsHero";
import ComingUpBoard from "./ComingUpBoard";
import WaitTimesScroll from "./WaitTimesScroll";
import WeatherTile from "./WeatherTile";
import { database } from "../../config/firebaseConfig";
import { useEffect, useState } from "react";
import { getBackgroundByEST } from "./MultiView/multi-view.utils";

// Every park WaitSigns covers. Edit here if the lineup ever changes.
const ALL_PARK_KEYS = ["MK", "EPCOT", "HS", "AK", "DL", "CA"];

// Layout math — identical to Dashboard.jsx display mode.
const STAGE_W = 1280;
const STAGE_H = 714;
const HEADER_H = 86;
const BG = "#e3e8ef";
const HEADER_NAVY = "#1a3054";

// Empty-frame card look — matches the real tiles' card styling.
const EMPTY_FRAME_STYLE = {
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 20,
  overflow: "hidden",
  border: "1px solid rgba(11,31,53,0.12)",
  boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
  background: "#ffffff",
};

const BroadcastBoard = () => {
  // Day/night background — same image + same flip schedule as the device
  // dash and multiview (getBackgroundByEST, re-checked every 60s). Runs on
  // the droplet; devices just see it in the video.
  const [backgroundImage, setBackgroundImage] = useState(getBackgroundByEST());
  useEffect(() => {
    const id = setInterval(() => {
      setBackgroundImage(getBackgroundByEST());
    }, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: BG,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER — same navy band, no name, no EXIT */}
      <div
        style={{
          height: HEADER_H,
          background: HEADER_NAVY,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 28px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              color: "#fff",
              fontFamily: "Lato, sans-serif",
              fontSize: 23,
              fontWeight: 500,
            }}
          >
            Have a Magical Day
          </span>
          {/* Family-name line intentionally empty — device overlays it.
              Kept as a spacer so the small line sits at the same y as on
              device dashboards. */}
          <span
            style={{
              fontFamily: "Lato, sans-serif",
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {"\u00A0"}
          </span>
        </div>
      </div>

      {/* STAGE AREA */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: STAGE_W,
            height: STAGE_H,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Park Pills Row */}
          <ParkPills
            database={database}
            subscribedParkKeys={ALL_PARK_KEYS}
            style={{ padding: "12px 16px", flexShrink: 0 }}
          />

          {/* Content area */}
          <div
            style={{
              flex: 1,
              display: "flex",
              gap: 14,
              padding: "0 16px 16px",
              minHeight: 0,
            }}
          >
            {/* Left column */}
            <div
              style={{
                flex: "0 0 58%",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                minHeight: 0,
              }}
            >
              <div style={{ height: 320, flexShrink: 0 }}>
                <NewsHero database={database} />
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ComingUpBoard
                  database={database}
                  subscribedParkKeys={ALL_PARK_KEYS}
                />
              </div>
            </div>

            {/* Right column */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 14,
                minHeight: 0,
              }}
            >
              <div style={{ height: 190, flexShrink: 0 }}>
                <WaitTimesScroll
                  database={database}
                  subscribedParkKeys={ALL_PARK_KEYS}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 14,
                  height: 130,
                  flexShrink: 0,
                }}
              >
                {/* Vacation countdown — EMPTY frame, device overlays */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={EMPTY_FRAME_STYLE} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <WeatherTile
                    database={database}
                    subscribedParkKeys={ALL_PARK_KEYS}
                  />
                </div>
              </div>

              {/* Live tile — EMPTY frame, device embeds its own player */}
              <div style={{ flex: 1, minHeight: 0 }}>
                <div style={EMPTY_FRAME_STYLE} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastBoard;
